"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { clienteApi } from "@/services/clienteApi";

const ESTADO_INICIAL = {
    nombre: "",
    email: "",
    mensaje: "",
    aceptaPrivacidad: false,
};

type EstadoEnvio = "inactivo" | "enviando" | "exito" | "error";

export default function FormularioContactoLegal() {
    const [formulario, setFormulario] = useState(ESTADO_INICIAL);
    const [estadoEnvio, setEstadoEnvio] = useState<EstadoEnvio>("inactivo");

    const formularioValido =
        formulario.nombre.trim() !== "" &&
        formulario.email.trim() !== "" &&
        formulario.mensaje.trim() !== "" &&
        formulario.aceptaPrivacidad;

    const manejarCambio = (campo: keyof typeof ESTADO_INICIAL, valor: string | boolean) => {
        setFormulario((prev) => ({ ...prev, [campo]: valor }));
    };

    const manejarEnvio = async (evento: FormEvent) => {
        evento.preventDefault();
        if (!formularioValido) return;

        setEstadoEnvio("enviando");

        try {
            const respuesta = await clienteApi.crudo("/api/contacto", {
                method: "POST",
                cuerpo: {
                    nombre: formulario.nombre,
                    email: formulario.email,
                    mensaje: formulario.mensaje,
                    origen: "pagina-legal",
                },
            });

            if (!respuesta.ok) throw new Error("Error al enviar");

            setEstadoEnvio("exito");
            setFormulario(ESTADO_INICIAL);
        } catch {
            setEstadoEnvio("error");
        }
    };

    if (estadoEnvio === "exito") {
        return (
            <div className="text-center py-8 bg-surface-2 rounded-3xl border-2 border-dashed border-ink shadow-sm">
                <span className="material-symbols-outlined text-4xl text-on-tint-success mb-3 block" aria-hidden="true">check_circle</span>
                <p className="text-ink font-bold text-lg mb-2">¡Mensaje enviado!</p>
                <p className="text-ink/70 text-sm">Te responderemos en un máximo de 48 horas laborables a tu correo.</p>
            </div>
        );
    }

    return (
        <div className="bg-surface-2 rounded-3xl border-2 border-dashed border-ink shadow-sm p-6 md:p-8">
            <p className="text-ink/80 mb-6 font-bold text-center">
                ¿Tienes alguna pregunta sobre nuestras condiciones?
            </p>

            <form onSubmit={manejarEnvio} className="space-y-4 max-w-lg mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                        type="text"
                        placeholder="Tu nombre"
                        value={formulario.nombre}
                        onChange={(e) => manejarCambio("nombre", e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-ink/20 bg-tint-neutral text-ink text-sm font-medium focus:outline-none focus:border-ink transition-colors"
                    />
                    <input
                        type="email"
                        placeholder="Tu email"
                        value={formulario.email}
                        onChange={(e) => manejarCambio("email", e.target.value)}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-ink/20 bg-tint-neutral text-ink text-sm font-medium focus:outline-none focus:border-ink transition-colors"
                    />
                </div>

                <textarea
                    placeholder="Escribe tu consulta..."
                    value={formulario.mensaje}
                    onChange={(e) => manejarCambio("mensaje", e.target.value)}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border-2 border-ink/20 bg-tint-neutral text-ink text-sm font-medium focus:outline-none focus:border-ink transition-colors resize-none"
                />

                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={formulario.aceptaPrivacidad}
                        onChange={(e) => manejarCambio("aceptaPrivacidad", e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-2 border-ink/30 accent-ink cursor-pointer"
                    />
                    <span className="text-xs text-ink/70 leading-relaxed">
                        He leído y acepto la{" "}
                        <Link href="/privacidad" className="text-blue-600 underline font-bold">
                            Política de Privacidad
                        </Link>
                        . Consiento el tratamiento de mis datos para gestionar mi consulta.
                    </span>
                </label>

                {estadoEnvio === "error" && (
                    <p className="text-on-tint-danger text-sm font-medium text-center">
                        Ha ocurrido un error. Inténtalo de nuevo o escríbenos a info@therapyrecap.com
                    </p>
                )}

                <div className="text-center pt-2">
                    <button
                        type="submit"
                        disabled={!formularioValido || estadoEnvio === "enviando"}
                        className="inline-block px-6 py-3 rounded-xl bg-ink text-canvas font-bold border-2 border-ink shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[4px_4px_0px_0px_var(--shadow-color)]"
                    >
                        {estadoEnvio === "enviando" ? "Enviando..." : "Enviar consulta"}
                    </button>
                </div>
            </form>
        </div>
    );
}
